// PR 5: Per-coin NOWPayments minimum order amount in USD.
// Fetched once via the crypto-min-amounts edge function (server-side proxy
// to avoid exposing the NOWPayments API key) and cached for 5 minutes.
// Falls back to conservative defaults so the Checkout picker still renders
// "Min: $X" even if the fetch fails or the user is offline.

import { supabase } from './supabase';

export interface CoinMinAmounts {
  BTC: number;
  USDT: number;
  LTC: number;
  XMR: number;
}

// Conservative fallbacks — slightly above typical NOWPayments mins so we
// never under-promise. If actual mins are lower, the user just sees a
// safe ceiling; if higher, the create-invoice pre-check still catches it.
const FALLBACK: CoinMinAmounts = {
  BTC: 25,
  USDT: 12,
  LTC: 1,
  XMR: 7,
};

let cached: CoinMinAmounts | null = null;
let cachedAt = 0;
const TTL_MS = 5 * 60 * 1000;

export async function getCoinMinAmounts(): Promise<CoinMinAmounts> {
  if (cached && Date.now() - cachedAt < TTL_MS) return cached;

  try {
    const { data, error } = await supabase.functions.invoke('crypto-min-amounts', {
      body: {},
    });
    if (error || !data || typeof data !== 'object') throw error ?? new Error('empty response');

    cached = { ...FALLBACK, ...(data as Partial<CoinMinAmounts>) };
    cachedAt = Date.now();
    return cached;
  } catch {
    return FALLBACK;
  }
}
